import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import '../css/AdminScreens.css';

const ScreenModal = ({ screen, onClose }) => {
  const [roleType, setRoleType] = useState(screen ? screen.role_type : '');
  const [roleCd, setRoleCd] = useState(screen ? screen.role_cd : '');
  const [selectedViews, setSelectedViews] = useState([]);
  const [grantedYn, setGrantedYn] = useState(screen ? (screen.granted_yn === 'TRUE' || screen.granted_yn === true) : false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 역할 유형 옵션
  const roleTypes = [
    { value: 'MASTER', label: '총괄관리자' },
    { value: 'SUB_MASTER', label: '부관리자' },
    { value: 'MEMBER', label: '일반회원' }
  ];

  // 역할 코드 옵션
  const roleCodes = {
    MASTER: [
      { value: 'MASTER', label: '총괄관리자' }
    ],
    SUB_MASTER: [
      { value: 'SUB_MASTER', label: '그룹 관리자' },
      { value: 'CENTER_ADMIN', label: '센터 관리자' }
    ],
    MEMBER: [
      { value: 'MEMBER', label: '일반회원' }
    ]
  };

  // 화면 코드 옵션
  const viewCodes = [
    { value: 'C', label: '관제센터' },
    { value: 'D', label: '대시보드' },
    // { value: 'U', label: '사용자 조회' },
    { value: 'M', label: '메시지 관리' },
    { value: 'O', label: '운영 관리' }
  ];

  useEffect(() => {
    if (screen && screen.view_cd) {
      const viewArray = screen.view_cd.split(',').map(v => v.trim());
      setSelectedViews(viewArray);
    }
    if (screen) {
      setGrantedYn(screen.granted_yn === 'TRUE' || screen.granted_yn === true);
    }
  }, [screen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roleType || !roleCd || selectedViews.length === 0) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    try {
      const formData = {
        role_type: roleType,
        role_cd: roleCd,
        view_cd: selectedViews.join(','),
        granted_yn: grantedYn ? 'TRUE' : 'FALSE'
      };

      let response;
      if (screen && screen.id) {
        // 수정 API 호출
        response = await axios.put(`${API_BASE_URL}/api/center-view-access-control/${screen.id}`, formData);
      } else {
        // 생성 API 호출
        response = await axios.post(`${API_BASE_URL}/api/center-view-access-control`, formData);
      }

      if (response.status === 200) {
        onClose(true);
      }
    } catch (error) {
      setError('저장 중 오류가 발생했습니다.');
      console.error('Error:', error);
    }
  };

  const handleDelete = async () => {
    if (!screen || !screen.id) return;

    if (!window.confirm('정말로 이 화면 권한을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/center-view-access-control/${screen.id}`);

      if (response.status === 200) {
        onClose(true);
      }
    } catch (error) {
      setError('삭제 중 오류가 발생했습니다.');
      console.error('Error:', error);
    }
  };

  const handleViewChange = (value) => {
    setSelectedViews(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  return (
    <div className="sds-modal-overlay" onClick={onClose}>
      <div className="sds-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="screen-modal-title">
        <div className="sds-modal__head">
          <h2 id="screen-modal-title" className="sds-modal__title">{screen ? '화면 권한 수정' : '새 화면 권한 생성'}</h2>
          <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onClose} aria-label="닫기"><X size={16} aria-hidden="true" /></button>
        </div>

        {/* 아래 폼 안의 버튼은 type 없이 만들어졌다 — 만들 때는 전역 CSS 때문에 type 을 쓸 수 없었다.
            그래서 제출용(저장/수정)만 기본 submit 으로 두고, 그 외(취소·삭제·세그먼트·화면 권한 칸)는
            onClick 에서 e.preventDefault() 로 제출을 막는다. 지금 규칙(design.md 6.7)은 폼 안의 보조
            버튼에 type="button" 을 쓰는 것이다 — 이 폼을 고칠 때 그쪽으로 맞춘다. */}
        <form onSubmit={handleSubmit}>
          <div className="sds-modal__body">
            {error && <div className="sds-notice sds-notice--error">{error}</div>}

            <div className="sds-field">
              <label className="sds-label">역할 유형</label>
              <select
                className="sds-select"
                value={roleType}
                onChange={(e) => setRoleType(e.target.value)}
              >
                <option value="">선택하세요</option>
                {roleTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sds-field">
              <label className="sds-label">역할 코드</label>
              <select
                className="sds-select"
                value={roleCd}
                onChange={(e) => setRoleCd(e.target.value)}
              >
                <option value="">선택하세요</option>
                {roleCodes[roleType]?.map(code => (
                  <option key={code.value} value={code.value}>
                    {code.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sds-field">
              <label className="sds-label">권한 여부</label>
              <div className="sds-seg" role="radiogroup" aria-label="권한 여부">
                {[['TRUE', '허용'], ['FALSE', '거부']].map(([value, label]) => {
                  const active = (value === 'TRUE') === grantedYn;
                  return (
                    <button
                      key={value}
                      role="radio"
                      aria-checked={active}
                      className={`sds-seg__item${active ? ' is-active' : ''}`}
                      onClick={(e) => { e.preventDefault(); setGrantedYn(value === 'TRUE'); }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="sds-field">
              <label className="sds-label">화면 권한</label>
              <div className="screens-page__perms">
                {viewCodes.map((view) => {
                  const on = selectedViews.includes(view.value);
                  return (
                    <button
                      key={view.value}
                      role="checkbox"
                      aria-checked={on}
                      className={`screens-page__perm${on ? ' is-on' : ''}`}
                      onClick={(e) => { e.preventDefault(); handleViewChange(view.value); }}
                    >
                      <span className={`screens-page__check${on ? ' is-on' : ''}`} aria-hidden="true" />
                      {view.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="sds-modal__foot">
            {screen && screen.id && (
              <button
                className="sds-btn sds-btn--danger"
                onClick={(e) => { e.preventDefault(); handleDelete(); }}
                disabled={loading}
              >
                삭제
              </button>
            )}
            <span className="sds-toolbar__spacer" />
            <button
              className="sds-btn sds-btn--secondary"
              onClick={(e) => { e.preventDefault(); onClose(); }}
              disabled={loading}
            >
              취소
            </button>
            <button
              className="sds-btn sds-btn--primary"
              disabled={loading}
            >
              {loading ? '처리 중...' : (screen ? '수정' : '저장')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScreenModal;
